/* eslint-disable */
import { useState, useRef, useEffect } from "react";
import { usd, pct, C, styles } from "./tokens";
import { SectionHeader, Callout, StatCard } from "./primitives";
import { runScenario } from "../engine/scenario";

// ─── EFFICIENT FRONTIER ───────────────────────────────────────────────────────
// X: SPIA allocation (% of RMD), 0–100 in 5% steps.
// Y: bond sleeve BEFORE purchase (% of IRA), 0–yMax in 2% steps.
// Each point is compared to a baseline holding the same bond sleeve untouched,
// so the colour shows only what annuitizing out of that sleeve did.
export function EfficientFrontierChart({ client, iraAssets, equityRate, bondRate, spiaTerm, spiaRate, arbitrageRate, fia = null, rebalanceYears = [], yMax = 60, onFrontierReady }) {
  const [frontierData, setFrontierData] = useState(null);
  const [running, setRunning] = useState(false);
  const [tooltip, setTooltip] = useState(null);
  const [metric, setMetric] = useState("wealth");
  const canvasRef = useRef();
  const ALLOC_STEPS = 21, Y_STEP = 2;

  const runModel = () => {
    if (!client) return;
    setRunning(true);
    setTimeout(() => {
      const currentYear = new Date().getFullYear();
      const ageYE = currentYear - new Date(client.dob).getFullYear();
      const points = [];
      for (let bp = 0; bp <= yMax; bp += Y_STEP) {
        const bondPct = bp / 100;
        const base = runScenario({ startBalance: iraAssets, ageYE, currentYear, returns: { mode: "assumed", equityRate, bondRate, bondPct }, spia: { allocationPct: 0, term: spiaTerm, rate: spiaRate, arbitrageRate }, horizon: 10 });
        const bWealth = (base.baseline[9]?.IRAValue || 0) + base.baseline.reduce((s, r) => s + r.RMD, 0);
        const bValue = base.baseline[9]?.IRAValue || 0;
        const bRMD = base.baseline.reduce((s, r) => s + r.RMD, 0);
        for (let ai = 0; ai < ALLOC_STEPS; ai++) {
          const alloc = ai / (ALLOC_STEPS - 1);
          const r = runScenario({ startBalance: iraAssets, ageYE, currentYear, returns: { mode: "assumed", equityRate, bondRate, bondPct }, spia: { allocationPct: alloc, term: spiaTerm, rate: spiaRate, arbitrageRate, rebalanceYears }, fia, horizon: 10 });
          const spiaWealth = (r.spiaProj[9]?.IRAValue || 0) + (r.spiaProj[9]?.SPIAPV || 0) + r.spiaProj.reduce((s, p) => s + p.TotalSPIARMD, 0);
          const spiaValue = r.spiaProj[9]?.IRAValue || 0;
          const spiaRMD = r.spiaProj.reduce((s, p) => s + p.TotalSPIARMD, 0);
          points.push({
            x: Math.round(alloc * 100), y: bp,
            postRoR: r.spiaProj[0]?.Return || 0, baseRoR: r.baseRate,
            premium: r.premium, premiumFromFixed: r.premiumFromFixed,
            wealthDelta: (spiaWealth - bWealth) / (bWealth || 1) * 100,
            valueDelta: (spiaValue - bValue) / (bValue || 1) * 100,
            rmdDelta: (spiaRMD - bRMD) / (bRMD || 1) * 100,
            spiaWealth, spiaValue, spiaRMD, baseWealth: bWealth, baseValue: bValue, baseRMD: bRMD,
          });
        }
      }
      const fd = { points, yMax, fia: fia && fia.allocationPct > 0 ? { ...fia } : null, equityRate, bondRate };
      setFrontierData(fd);
      if (onFrontierReady) onFrontierReady(fd, metric);
      setRunning(false);
    }, 20);
  };

  const getDelta = (pt) => metric === "value" ? pt.valueDelta : metric === "rmd" ? pt.rmdDelta : pt.wealthDelta;

  useEffect(() => {
    if (!frontierData || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const W = canvas.width, H = canvas.height;
    const PAD = { top: 28, right: 36, bottom: 58, left: 76 };
    const plotW = W - PAD.left - PAD.right, plotH = H - PAD.top - PAD.bottom;
    ctx.clearRect(0, 0, W, H); ctx.fillStyle = "#f8fafc"; ctx.fillRect(0, 0, W, H);
    const cxFn = v => PAD.left + (v / 100) * plotW;
    const cyFn = v => PAD.top + plotH - (v / yMax) * plotH;
    ctx.strokeStyle = "#e2e8f0"; ctx.lineWidth = 0.5;
    for (let y = 0; y <= yMax; y += 10) { ctx.beginPath(); ctx.moveTo(PAD.left, cyFn(y)); ctx.lineTo(PAD.left + plotW, cyFn(y)); ctx.stroke(); }
    for (let x = 0; x <= 100; x += 10) { ctx.beginPath(); ctx.moveTo(cxFn(x), PAD.top); ctx.lineTo(cxFn(x), PAD.top + plotH); ctx.stroke(); }
    const pts = frontierData.points;
    const maxAbs = Math.max(...pts.map(p => Math.abs(getDelta(p))), 0.01);
    pts.forEach(pt => {
      const d = getDelta(pt), frac = Math.min(Math.abs(d) / maxAbs, 1), r = 2.5 + frac * 9;
      ctx.beginPath(); ctx.arc(cxFn(pt.x), cyFn(pt.y), r, 0, Math.PI * 2);
      if (d >= 0) { ctx.fillStyle = `rgba(5,150,105,${0.18 + frac * 0.72})`; ctx.strokeStyle = `rgba(4,120,87,${0.3 + frac * 0.6})`; }
      else { ctx.fillStyle = `rgba(225,29,72,${0.18 + frac * 0.72})`; ctx.strokeStyle = `rgba(190,18,60,${0.3 + frac * 0.6})`; }
      ctx.lineWidth = 0.7; ctx.fill(); ctx.stroke();
    });
    ctx.fillStyle = "#64748b"; ctx.font = "10px Inter, Arial"; ctx.textAlign = "center";
    for (let x = 0; x <= 100; x += 10) ctx.fillText(x + "%", cxFn(x), H - PAD.bottom + 16);
    ctx.font = "11px Inter, Arial"; ctx.fillText("SPIA Allocation (% of RMD)", PAD.left + plotW / 2, H - 8);
    ctx.textAlign = "right"; ctx.font = "10px Inter, Arial";
    for (let y = 0; y <= yMax; y += 10) ctx.fillText(y + "%", PAD.left - 7, cyFn(y) + 4);
    ctx.save(); ctx.translate(14, PAD.top + plotH / 2); ctx.rotate(-Math.PI / 2);
    ctx.textAlign = "center"; ctx.font = "11px Inter, Arial"; ctx.fillText("Bond sleeve before purchase (% of IRA)", 0, 0); ctx.restore();
  }, [frontierData, metric]);

  const handleMouse = (e) => {
    if (!frontierData || !canvasRef.current) return;
    const canvas = canvasRef.current, rect = canvas.getBoundingClientRect();
    const mx = (e.clientX - rect.left) * (canvas.width / rect.width), my = (e.clientY - rect.top) * (canvas.height / rect.height);
    const PAD = { top: 28, right: 36, bottom: 58, left: 76 };
    const plotW = canvas.width - PAD.left - PAD.right, plotH = canvas.height - PAD.top - PAD.bottom;
    const cxFn = v => PAD.left + (v / 100) * plotW, cyFn = v => PAD.top + plotH - (v / yMax) * plotH;
    let best = null, bd = 12;
    frontierData.points.forEach(pt => { const dx = mx - cxFn(pt.x), dy = my - cyFn(pt.y), d = Math.hypot(dx, dy); if (d < bd) { bd = d; best = pt; } });
    setTooltip(best ? { pt: best, x: e.clientX - rect.left, y: e.clientY - rect.top } : null);
  };

  const metricLabel = { wealth: "Total Wealth Δ", value: "Portfolio Value Δ", rmd: "Total RMD Δ" };
  const pts = frontierData?.points || [];
  const best = pts.length ? pts.reduce((a, b) => getDelta(b) > getDelta(a) ? b : a) : null;
  const worst = pts.length ? pts.reduce((a, b) => getDelta(b) < getDelta(a) ? b : a) : null;

  return (
    <div style={styles.card}>
      <SectionHeader icon="📡" title="Efficient Frontier" subtitle="SPIA allocation × bond sleeve — where annuitizing the fixed income pays" />
      <Callout type="blue">
        Each dot runs the 204 plan with the premium drawn from the bond sleeve first and compares it to a baseline that keeps that same sleeve.
        Green = ahead of its own baseline; red = behind. Equity {pct(equityRate)}, bonds {pct(bondRate)} — set in Scenario inputs.
      </Callout>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <button style={styles.btnPrimary} onClick={runModel} disabled={running || !client}>{running ? "Running…" : "Run Efficient Frontier"}</button>
        {frontierData && [["wealth", "Total Wealth"], ["value", "Portfolio Value"], ["rmd", "Total RMD"]].map(([id, lbl]) => (
          <button key={id} onClick={() => { setMetric(id); if (onFrontierReady) onFrontierReady(frontierData, id); }}
            style={{ ...styles.btnSecondary, background: metric === id ? C.navy : "#fff", color: metric === id ? "#fff" : C.textSec }}>{lbl}</button>
        ))}
      </div>
      {frontierData && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 12 }}>
            {best && <StatCard accent label="Best" value={`${best.x}% alloc / ${best.y}% bonds`} sublabel={`${metricLabel[metric]} ${best && getDelta(best) >= 0 ? "+" : ""}${getDelta(best).toFixed(2)}%`} />}
            {worst && <StatCard label="Worst" value={`${worst.x}% alloc / ${worst.y}% bonds`} sublabel={`${metricLabel[metric]} ${getDelta(worst).toFixed(2)}%`} />}
          </div>
          <div style={{ position: "relative" }}>
            <canvas ref={canvasRef} width={760} height={420} style={{ width: "100%", borderRadius: 10, border: `1px solid ${C.borderLight}` }}
              onMouseMove={handleMouse} onMouseLeave={() => setTooltip(null)} />
            {tooltip && (
              <div style={{ position: "absolute", left: Math.min(tooltip.x + 12, 520), top: tooltip.y + 12, background: "#0f172a", color: "#fff", padding: "10px 12px", borderRadius: 8, fontSize: 12, pointerEvents: "none", minWidth: 220, zIndex: 5 }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>SPIA {tooltip.pt.x}% of RMD · Bonds {tooltip.pt.y}% of IRA</div>
                <div style={{ opacity: 0.8 }}>Premium {usd(tooltip.pt.premium)} ({usd(tooltip.pt.premiumFromFixed)} from bonds)</div>
                <div style={{ opacity: 0.8 }}>Managed RoR after: {pct(tooltip.pt.postRoR)} (baseline {pct(tooltip.pt.baseRoR)})</div>
                <div style={{ color: getDelta(tooltip.pt) >= 0 ? "#6ee7b7" : "#fca5a5", fontWeight: 700, marginTop: 4 }}>{metricLabel[metric]}: {getDelta(tooltip.pt) >= 0 ? "+" : ""}{getDelta(tooltip.pt).toFixed(2)}%</div>
                <div style={{ opacity: 0.75, lineHeight: 1.7, fontSize: 11 }}>10-yr value {usd(tooltip.pt.spiaValue)} · cum. RMD {usd(tooltip.pt.spiaRMD)} · wealth {usd(tooltip.pt.spiaWealth)}</div>
              </div>
            )}
          </div>
          <div style={{ textAlign: "center", marginTop: 8, fontSize: 11, color: C.textMuted }}>
            Bottom row (0% bonds) = nothing to displace: any red there is the pure cost of annuitizing equity. Higher rows show the benefit growing with the sleeve replaced.
          </div>
        </>
      )}
    </div>
  );
}
